// src/api/attendance.ts
import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../../environment";

const ATTENDANCE_BASE_URL = `${API_BASE_PATH}/attendance`;

const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, "");
  }

  return detail || details || message;
};

// 🧾 Response Structure - Updated to match your backend
export interface AttendanceRecord {
  id: number;
  date: number | string;
  employee_id: string | number;
  username: string;
  check_in: string | null;
  check_out: string | null;
  punch_in: string | null;
  punch_out: string | null;
  working_hours: string | null;
  today_status: number;
  idl: number;
  production_hr: string | null;
  status: string;
  Break: string | null;
  late: string | null;
  earlyout: string | null;
  over_time: string | null;
  regularized: boolean;
  shift_type_id: number | null;
  source: string;
  created_date?: string;
  attendance_date?: string;
}

// 🧾 Response type for widget stats
export interface AttendanceWidgetStats {
  month: number;
  year: number;
  day?: number | null;
  employee_id?: string | null;
  absent: number;
  present: number;
  late: number;
  earlyout: number;
  missed_punchout: number;
  regularized: number;
  device: number;
  online: number;
  in_time: number;
  out_time: number;
  shift_absent: {
    [shift: string]: number;
  };
  shift_present: {
    [shift: string]: number;
  };
  Total_employee: number;
  shift_assign_count: {
    [shift: string]: number;
  };
  all_employee: number;
  Total_absent: number;
}

// ✅ POST - Check-in
export const postAttendanceSignIn = async (token: string | null): Promise<any> => {
  try {
    const response = await axios.post(
      `${ATTENDANCE_BASE_URL}/signin`,
      {},
      {
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    toast.success("Checked in successfully");
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    console.error("Check-in API error:", message);
    throw { message };
  }
};

// ✅ PUT - Check-out
export const putAttendanceSignOut = async (token: string | null): Promise<any> => {
  try {
    const response = await axios.put(
      `${ATTENDANCE_BASE_URL}/signout`,
      {},
      {
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    toast.success("Checked out successfully");
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    console.error("Check-out API error:", message);
    throw { message };
  }
};

// ✅ GET - All attendance records
export const getAllAttendance = async (token: string | null): Promise<AttendanceRecord[]> => {
  try {
    const response = await axios.get(`${ATTENDANCE_BASE_URL}/all`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data || [];
  } catch (error: any) {
    const message = extractErrorMessage(error);
    console.error("Get attendance API error:", message);
    throw { message };
  }
};

// ✅ GET - Self attendance
export const getSelfAttendance = async (token: string | null): Promise<AttendanceRecord[]> => {
  try {
    const response = await axios.get(`${ATTENDANCE_BASE_URL}/self`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data || [];
  } catch (error: any) {
    const message = extractErrorMessage(error);
    console.error("Get self attendance API error:", message);
    throw { message };
  }
};

// ✅ GET - Attendance widgets
export const getAttendanceWidgets = async (
  month: number,
  year: number,
  targetDate: unknown,
  day?: number | null,
  employee_id?: string | null,
  token?: string
): Promise<AttendanceWidgetStats> => {
  try {
    const queryParams = new URLSearchParams({
      month: String(month),
      year: String(year),
    });

    if (day != null) queryParams.append("day", String(day));
    if (employee_id != null) queryParams.append("employee_id", employee_id);

    const url = `${ATTENDANCE_BASE_URL}/attendance/widgets?${queryParams.toString()}`;

    const response = await axios.get(url, {
      headers: {
        accept: "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    return response.data as AttendanceWidgetStats;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    console.error("Get attendance widgets API error:", message);
    throw { message };
  }
};

// 🧾 Response type for Working Hours
export interface AttendanceWorkingHours {
  today: string;
  total_hours_today: string;
  total_hours_week: string;
  total_hours_month: string;
  overtime_month: string;
  late: string;
}

// ✅ GET - Working Hours
export const getAttendanceWorkingHours = async (
  employee_id: string,
  month: number,
  year: number,
  day: number,
  token: string
): Promise<AttendanceWorkingHours> => {
  try {
    const queryParams = new URLSearchParams({
      employee_id: employee_id,
      month: String(month),
      year: String(year),
      day: String(day),
    });

    const url = `${ATTENDANCE_BASE_URL}/attendance/working_hours?${queryParams.toString()}`;

    const response = await axios.get(url, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data as AttendanceWorkingHours;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    console.error("Get working hours API error:", message);
    throw { message };
  }
};
